import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { depositText, DepositText } from '../i18n/pages/deposit';
import {
  CLAIM_REASONS,
  DepositClaimReason,
  DepositDetail,
  claimDeposit,
  getDeposit,
  isSettled,
  releaseDeposit,
  waiveDeposit,
} from '../api.deposit';

interface Props {
  reservationId: number;
}

/** The manager's picker label and the sentence the guest actually reads, per reason code. */
const reasonLabel = (reason: DepositClaimReason, t: DepositText): string => ({
  DAMAGE: t.mDamage, MISSING_ITEMS: t.mMissing, EXTRA_CLEANING: t.mCleaning,
  SMOKING: t.mSmoking, EXTRA_GUESTS: t.mGuests, LATE_CHECKOUT: t.mLate, OTHER: t.mOther,
}[reason]);

const reasonSentence = (reason: DepositClaimReason, t: DepositText): string => ({
  DAMAGE: t.gDamage, MISSING_ITEMS: t.gMissing, EXTRA_CLEANING: t.gCleaning,
  SMOKING: t.gSmoking, EXTRA_GUESTS: t.gGuests, LATE_CHECKOUT: t.gLate, OTHER: t.gOther,
}[reason]);

/**
 * The deposit on one reservation, and the two decisions that end it.
 *
 * <p>Both decisions confirm, not just the one that takes money. Releasing looks like the safe tap
 * and is not: a cancelled authorization cannot be placed again, so a mis-tap throws the protection
 * away for good. The prompts render in place rather than as {@code window.confirm()}, for the same
 * reason the stay-extension withdraw prompt does — a browser dialog speaks the browser's language
 * and would be the one untranslated string on the screen.
 */
const DepositBlock: React.FC<Props> = ({ reservationId }) => {
  const { lang } = useLang();
  const t = depositText[lang];

  const [deposit, setDeposit] = useState<DepositDetail | null>(null);
  const [mode, setMode] = useState<'none' | 'keep'>('none');
  const [keepRaw, setKeepRaw] = useState('');
  const [reason, setReason] = useState<DepositClaimReason>('DAMAGE');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState<'release' | 'keep' | 'waive' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDeposit(await getDeposit(reservationId));
    } catch {
      // No deposit on this reservation, or not this manager's. Either way: render nothing.
      setDeposit(null);
    }
  }, [reservationId]);

  useEffect(() => { void load(); }, [load]);

  if (!deposit) return null;

  const currency = deposit.currency;
  const money = (v: number | null) =>
    v === null ? '' : `${v.toLocaleString(lang === 'en' ? 'en-GB' : lang)} ${currency}`;
  const fill = (s: string, v: Record<string, string>) =>
    Object.entries(v).reduce((a, [k, x]) => a.replace(`{${k}}`, x), s);
  const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '');
  const at = (iso: string | null) => (iso
    ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '');

  const keep = Number(keepRaw.replace(/[^\d.]/g, '')) || 0;
  const rest = Math.max(0, deposit.amount - keep);
  const keepValid = keep > 0 && keep <= deposit.amount;

  const act = async (run: () => Promise<DepositDetail>) => {
    setBusy(true);
    setError(null);
    try {
      setDeposit(await run());
      setMode('none');
      setConfirming(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
    } finally {
      setBusy(false);
    }
  };

  // ── Settled: say what happened and stop ──────────────────────────────────
  if (isSettled(deposit.status)) {
    const line = deposit.status === 'RELEASED'
      ? fill(t.outReleased, { date: day(deposit.decidedAt) })
      : deposit.status === 'CLAIMED'
        ? fill(t.outClaimed, {
            amount: money(deposit.capturedAmount),
            date: day(deposit.decidedAt),
            rest: money(deposit.amount - (deposit.capturedAmount ?? 0)),
          })
        : fill(t.outExpired, { date: day(deposit.decidedAt) });

    return (
      <div style={styles.wrap}>
        <h4 style={styles.h4}>🔒 {t.decideTitle}</h4>
        <div style={{ ...styles.outcome, ...(deposit.status === 'CLAIMED' ? styles.outcomeRed : styles.outcomeGreen) }}>
          {line}
        </div>
        {deposit.managerNote && <div style={styles.note}>{deposit.managerNote}</div>}
      </div>
    );
  }

  // ── Not held yet: the arrival is uncovered ───────────────────────────────
  if (deposit.status !== 'HELD') {
    return (
      <div style={styles.wrap}>
        <h4 style={styles.h4}>🔒 {t.decideTitle}</h4>

        {deposit.waivedAt ? (
          <div style={styles.outcomeGrey}>{t.waived}</div>
        ) : (
          <>
            <div style={styles.blocked}>
              <div style={styles.blockedTitle}>
                ⚠ {fill(t.guestFailedTitle, { amount: money(deposit.amount) })}
              </div>
              {deposit.lastHoldError && <div style={styles.err}>{deposit.lastHoldError}</div>}
              {deposit.holdAttempts ? (
                <div style={styles.small}>{deposit.holdAttempts}×</div>
              ) : null}
            </div>

            {/* The escape hatch, and the only thing that opens a door without money behind it.
                Never automatic — the system does not waive a deposit on anyone's behalf. */}
            {confirming === 'waive' ? (
              <div style={styles.confirm}>
                <div style={styles.confirmQ}>{t.waiveCta}?</div>
                <div style={styles.confirmWhy}>{t.waiveWhy}</div>
                <div style={styles.confirmRow}>
                  <button type="button" style={styles.btn} onClick={() => setConfirming(null)}>{t.back}</button>
                  <button
                    type="button" style={{ ...styles.btn, ...styles.btnPrimary }} disabled={busy}
                    onClick={() => act(() => waiveDeposit(deposit.depositId))}
                  >{t.waiveCta}</button>
                </div>
              </div>
            ) : (
              <button type="button" style={styles.linkBtn} onClick={() => setConfirming('waive')}>
                {t.waiveCta}
              </button>
            )}
          </>
        )}
        {error && <div style={styles.error}>{error}</div>}
      </div>
    );
  }

  // ── Held: the decision ───────────────────────────────────────────────────
  return (
    <div style={styles.wrap}>
      <h4 style={styles.h4}>🔒 {t.decideTitle}</h4>

      <div style={styles.held}>
        <span style={styles.heldAmount}>{money(deposit.amount)}</span>
        <span style={styles.heldMeta}>
          {fill(t.heldSince, { date: day(deposit.heldAt) })} · {t.notCharged}
        </span>
      </div>

      {deposit.holdExpiresAt && (
        <div style={styles.deadline}>
          ⏳ {fill(t.deadlineWarn, { date: at(deposit.holdExpiresAt) })}
        </div>
      )}

      {confirming === 'release' ? (
        <div style={styles.confirm}>
          <div style={styles.confirmQ}>{t.confirmRelease}</div>
          <div style={styles.confirmWhy}>{fill(t.confirmReleaseWhy, { amount: money(deposit.amount) })}</div>
          <div style={styles.confirmRow}>
            <button type="button" style={styles.btn} onClick={() => setConfirming(null)}>{t.back}</button>
            <button
              type="button" style={{ ...styles.btn, ...styles.btnPrimary }} disabled={busy}
              onClick={() => act(() => releaseDeposit(deposit.depositId))}
            >{t.yesRelease}</button>
          </div>
        </div>
      ) : confirming === 'keep' ? (
        <div style={styles.confirm}>
          <div style={styles.confirmQ}>{fill(t.confirmKeep, { amount: money(keep) })}</div>
          <div style={styles.confirmWhy}>
            {rest === 0 ? t.confirmKeepAllWhy : fill(t.confirmKeepWhy, { rest: money(rest) })}
          </div>
          <div style={styles.confirmRow}>
            <button type="button" style={styles.btn} onClick={() => setConfirming(null)}>{t.back}</button>
            <button
              type="button" style={{ ...styles.btn, ...styles.btnDanger }} disabled={busy}
              onClick={() => act(() => claimDeposit(deposit.depositId, {
                amount: keep, reason, managerNote: note.trim() || undefined,
              }))}
            >{fill(t.yesKeep, { amount: money(keep) })}</button>
          </div>
        </div>
      ) : (
        <>
          <div style={styles.choice}>
            <button
              type="button" style={{ ...styles.opt, ...(mode === 'none' ? styles.optOn : {}) }}
              onClick={() => setMode('none')}
            >
              <span style={styles.optName}>{t.releaseCta}</span>
              <span style={styles.optDesc}>{t.releaseDesc}</span>
            </button>
            <button
              type="button" style={{ ...styles.opt, ...(mode === 'keep' ? styles.optOn : {}) }}
              onClick={() => setMode('keep')}
            >
              <span style={styles.optName}>{t.keepCta}</span>
              <span style={styles.optDesc}>{t.keepDesc}</span>
            </button>
          </div>

          {mode === 'none' ? (
            <div style={styles.panel}>
              <div style={styles.split}>
                <span>{t.guestGetsBack}</span>
                <b>{money(deposit.amount)}</b>
              </div>
              <div style={styles.acts}>
                <button
                  type="button" style={{ ...styles.btn, ...styles.btnPrimary }} disabled={busy}
                  onClick={() => setConfirming('release')}
                >{t.releaseCta}</button>
              </div>
            </div>
          ) : (
            <div style={styles.panel}>
              <span style={styles.label}>{t.keepHowMuch}</span>
              <div style={styles.amountRow}>
                <input
                  type="text" inputMode="numeric" value={keepRaw} disabled={busy}
                  onChange={e => setKeepRaw(e.target.value)}
                  placeholder="0" style={styles.amountInput}
                />
                <span style={styles.unit}>{currency}</span>
                {[0.25, 0.5, 1].map(f => (
                  <button
                    key={f} type="button" style={styles.quick}
                    onClick={() => setKeepRaw(String(Math.round(deposit.amount * f)))}
                  >{f === 1 ? '100%' : `${f * 100}%`}</button>
                ))}
              </div>

              <div style={styles.split}>
                <span>{t.guestGetsBack}</span>
                <b>{money(rest)}</b>
              </div>

              <span style={styles.label}>{t.reasonLabel}</span>
              <div style={styles.reasons}>
                {CLAIM_REASONS.map(r => (
                  <button
                    key={r} type="button"
                    style={{ ...styles.reason, ...(reason === r ? styles.reasonOn : {}) }}
                    onClick={() => setReason(r)}
                  >{reasonLabel(r, t)}</button>
                ))}
              </div>

              <span style={styles.label}>{t.noteLabel}</span>
              <textarea
                value={note} disabled={busy} rows={2}
                onChange={e => setNote(e.target.value)}
                placeholder={t.notePlaceholder}
                style={styles.textarea}
              />

              {/* Built from the template, never typed. The guest reads this in their own language,
                  and a host annoyed about a broken table should not be writing it themselves. */}
              <div style={styles.preview}>
                <div style={styles.previewCap}>{t.guestSees}</div>
                <div style={styles.previewBody}>
                  {fill(t.guestKeptBody, {
                    amount: money(keep), total: money(deposit.amount), rest: money(rest),
                  })}{' '}{reasonSentence(reason, t)}
                </div>
              </div>

              <div style={styles.acts}>
                <button
                  type="button" style={{ ...styles.btn, ...styles.btnDanger }}
                  disabled={busy || !keepValid}
                  onClick={() => setConfirming('keep')}
                >{fill(t.yesKeep, { amount: money(keep) })}</button>
              </div>
            </div>
          )}
        </>
      )}

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 16 },
  h4: { margin: '0 0 12px', fontSize: 15, color: '#0f172a' },
  held: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  heldAmount: { fontSize: 30, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
  heldMeta: { fontSize: 13, color: '#64748b' },
  deadline: {
    padding: '10px 12px', borderRadius: 9, background: '#fffbeb',
    border: '1px solid #fcd34d', color: '#92400e', fontSize: 12.5, lineHeight: 1.5, marginBottom: 14,
  },
  choice: { display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 12 },
  opt: {
    flex: '1 1 200px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 3,
    padding: '11px 13px', borderRadius: 10, border: '1px solid #e2e8f0',
    background: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  optOn: { borderColor: '#2563eb', borderWidth: 2, background: '#eff6ff' },
  optName: { fontWeight: 700, fontSize: 14, color: '#0f172a' },
  optDesc: { fontSize: 12, color: '#64748b', lineHeight: 1.45 },
  panel: {
    border: '1px solid #e2e8f0', borderRadius: 10, padding: 14,
    background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 12,
  },
  label: {
    fontSize: 11, fontWeight: 700, letterSpacing: '.055em', textTransform: 'uppercase',
    color: '#94a3b8', display: 'block',
  },
  amountRow: { display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' },
  amountInput: {
    width: 120, padding: '8px 11px', borderRadius: 8, border: '1px solid #e2e8f0',
    fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit',
  },
  unit: { fontSize: 13, color: '#64748b' },
  quick: {
    fontSize: 12, fontWeight: 700, padding: '6px 10px', borderRadius: 7,
    border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontFamily: 'inherit',
  },
  split: {
    display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 13,
    padding: '9px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
  },
  reasons: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  reason: {
    fontSize: 12.5, padding: '6px 11px', borderRadius: 99, border: '1px solid #e2e8f0',
    background: '#fff', color: '#475569', cursor: 'pointer', fontFamily: 'inherit',
  },
  reasonOn: { background: '#2563eb', borderColor: '#2563eb', color: '#fff', fontWeight: 650 },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
    border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
  },
  preview: {
    border: '1px solid #e2e8f0', borderLeft: '3px solid #2563eb', borderRadius: '0 9px 9px 0',
    padding: '11px 13px', background: '#fff',
  },
  previewCap: {
    fontSize: 10.5, fontWeight: 700, letterSpacing: '.055em', textTransform: 'uppercase',
    color: '#94a3b8', marginBottom: 5,
  },
  previewBody: { fontSize: 13, color: '#1e293b', lineHeight: 1.55 },
  acts: { display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' },
  btn: {
    fontSize: 13.5, fontWeight: 650, padding: '8px 15px', borderRadius: 8,
    border: '1px solid #e2e8f0', background: '#fff', color: '#1e293b', cursor: 'pointer', fontFamily: 'inherit',
  },
  btnPrimary: { background: '#2563eb', borderColor: '#2563eb', color: '#fff' },
  btnDanger: { background: '#b91c1c', borderColor: '#b91c1c', color: '#fff' },
  confirm: {
    border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 10,
    padding: '13px 14px', display: 'flex', flexDirection: 'column', gap: 5,
  },
  confirmQ: { fontWeight: 700, fontSize: 14, color: '#0f172a' },
  confirmWhy: { fontSize: 12.5, color: '#92400e', lineHeight: 1.5 },
  confirmRow: { display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 6 },
  blocked: {
    padding: '11px 13px', borderRadius: 9, background: '#fef2f2',
    border: '1px solid #fecaca', marginBottom: 10,
  },
  blockedTitle: { fontWeight: 700, fontSize: 13.5, color: '#b91c1c' },
  err: { fontSize: 12, color: '#b91c1c', marginTop: 4 },
  small: { fontSize: 11.5, color: '#94a3b8', marginTop: 3 },
  linkBtn: {
    fontSize: 13, color: '#2563eb', background: 'none', border: 0,
    padding: 0, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline',
  },
  outcome: { padding: '11px 13px', borderRadius: 9, fontSize: 13.5, lineHeight: 1.55 },
  outcomeGreen: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' },
  outcomeRed: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' },
  outcomeGrey: { padding: '11px 13px', borderRadius: 9, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', fontSize: 13.5 },
  note: { marginTop: 8, fontSize: 12.5, color: '#64748b', fontStyle: 'italic' },
  error: { marginTop: 10, padding: '9px 12px', borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 13 },
};

export default DepositBlock;
